import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Key, Brain, Send, MapPin, Filter, CheckCircle, ArrowLeft, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const GuidePage: React.FC = () => {
    const { t } = useTranslation();

    const stepConfig = [
        { key: 'step_1', icon: MessageCircle, proPlan: false },
        { key: 'step_2', icon: Key, proPlan: false },
        { key: 'step_3', icon: Brain, proPlan: false },
        { key: 'step_4', icon: Sparkles, proPlan: true },
        { key: 'step_5', icon: MapPin, proPlan: false },
        { key: 'step_6', icon: Filter, proPlan: false },
        { key: 'step_7', icon: CheckCircle, proPlan: false },
        { key: 'step_8', icon: Send, proPlan: false },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6 lg:p-10 max-w-5xl mx-auto flex flex-col gap-8"
        >
            <div>
                <Link to="/documentacao" className="inline-flex items-center gap-2 text-primary hover:underline mb-4">
                    <ArrowLeft className="size-4" />
                    {t('guide.back_to_documentation')}
                </Link>

                <motion.h1
                    className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary mb-2"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                >
                    {t('guide.page_title')}
                </motion.h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                    {t('guide.page_subtitle')}
                </p>
            </div>

            {/* Steps */}
            <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-8 top-12 bottom-12 w-0.5 bg-gradient-to-b from-primary/50 to-primary/10 hidden md:block" />

                <div className="flex flex-col gap-8">
                    {stepConfig.map((config, index) => {
                        const Icon = config.icon;
                        const stepNumber = index + 1;
                        const title = t(`guide.steps.${config.key}.title`);
                        const description = t(`guide.steps.${config.key}.description`);
                        const tips = t(`guide.steps.${config.key}.tips`, { returnObjects: true }) as string[];

                        return (
                            <motion.div
                                key={stepNumber}
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                                className="relative"
                            >
                                <div className="card p-6 md:ml-16">
                                    {/* Step Number Circle */}
                                    <div className="absolute -left-8 top-6 md:left-0 size-16 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                                        <span className="text-white font-black text-2xl">{stepNumber}</span>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-6">
                                        {/* Icon */}
                                        <div className="hidden md:flex items-start">
                                            <div className="size-14 rounded-xl bg-primary/10 flex items-center justify-center">
                                                <Icon className="size-7 text-primary" />
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-grow space-y-4 ml-12 md:ml-0">
                                            <div>
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h2 className="text-2xl font-bold text-[#181411] dark:text-white">
                                                        {title}
                                                    </h2>
                                                    {config.proPlan && (
                                                        <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full">
                                                            {t('guide.pro_badge')}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                                    {description}
                                                </p>
                                            </div>

                                            {/* Tips */}
                                            {tips && tips.length > 0 && (
                                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-lg">💡</span>
                                                        <span className="font-semibold text-blue-900 dark:text-blue-300 text-sm">
                                                            {t('guide.tips_label')}
                                                        </span>
                                                    </div>
                                                    <ul className="space-y-1.5 ml-1">
                                                        {tips.map((tip, tipIndex) => (
                                                            <li key={tipIndex} className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
                                                                <span className="text-blue-500 mt-0.5">•</span>
                                                                <span>{tip}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Success Message */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="card p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800"
            >
                <div className="flex items-start gap-4">
                    <div className="size-12 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                        <CheckCircle className="size-7 text-white" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-green-900 dark:text-green-300 mb-2">
                            {t('guide.success_title')}
                        </h3>
                        <p className="text-green-800 dark:text-green-200">
                            {t('guide.success_message')}
                        </p>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default GuidePage;
